import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw, BookOpen } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { visualExplainer, type ExplainerCopy } from "@/data/visualExplainer";
import { explainerStory } from "@/data/explainerStory";
import implant from "@/assets/explainer/implant.asset.json";
import abutment from "@/assets/explainer/abutment.asset.json";
import crown from "@/assets/explainer/crown.asset.json";
import design from "@/assets/explainer/design.asset.json";
import darkMargin from "@/assets/explainer/dark-margin-detail.asset.json";
import joint from "@/assets/explainer/joint-detail.asset.json";

const CANONICAL = "https://eap-abutments.com/eap-erklaert";
const images = [implant, abutment, crown];

function KnowledgeCheck({ copy }: { copy: ExplainerCopy }) {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  return <section className="border-t border-border py-12">
    <div className="mx-auto max-w-5xl px-4">
      <h2 className="text-2xl md:text-3xl mb-8">{copy.quizTitle}</h2>
      <div className="grid md:grid-cols-3 gap-8">
        {copy.questions.map((question, index) => <fieldset key={question.question} className="min-w-0">
          <legend className="font-bold text-lg mb-4">{index + 1}. {question.question}</legend>
          <div className="space-y-3">{question.options.map((option, choice) => <label key={option} className="flex items-start gap-3 cursor-pointer">
            <input type="radio" name={`question-${index}`} checked={answers[index] === choice} onChange={() => setAnswers(previous => ({ ...previous, [index]: choice }))} className="mt-1.5 shrink-0 accent-primary" />
            <span>{option}</span>
          </label>)}</div>
          {answers[index] !== undefined && <p aria-live="polite" className="mt-4 border-l-2 border-primary pl-3 text-sm leading-relaxed"><strong>{copy.answer}: </strong>{question.explanation}</p>}
        </fieldset>)}
      </div>
      <Button variant="outline" className="mt-8" onClick={() => setAnswers({})}><RotateCcw className="w-4 h-4 mr-2" />{copy.reset}</Button>
    </div>
  </section>;
}

export default function VisualExplainer() {
  const { language } = useLanguage();
  const c = visualExplainer[language];
  const story = explainerStory[language];
  const stepImages = [darkMargin, design, joint, design];
  return <div className="min-h-screen bg-background">
    <Helmet>
      <title>{c.menu} | EAP Abutments®</title>
      <meta name="description" content={c.intro} />
      <link rel="canonical" href={CANONICAL} />
      <meta property="og:title" content={`${c.menu} | EAP Abutments®`} />
      <meta property="og:description" content={c.intro} />
      <meta property="og:url" content={CANONICAL} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary" />
    </Helmet>
    <Navigation />
    <main className="pt-28">
      <header className="mx-auto max-w-5xl px-4 pb-10">
        <Breadcrumbs items={[{ label: c.menu }]} />
        <h1 className="text-4xl md:text-5xl mt-6 mb-5 leading-tight">{c.menu}</h1>
        <p className="text-lg leading-relaxed max-w-3xl">{story.intro}</p>
      </header>
      <section className="bg-secondary/40 border-y border-border py-10">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl mb-8">{c.anatomyTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {c.parts.map(([name, text], index) => <figure key={name} className="min-w-0">
              <div className="h-72 flex items-center justify-center mb-5 bg-background rounded-lg border border-border">
                <img src={images[index]?.url} alt={name} className="h-60 max-w-[75%] object-contain" width="160" height="180" />
              </div>
              <figcaption><h3 className="text-xl mb-2"><span className="text-primary mr-2">0{index + 1}</span>{name}</h3><p className="leading-relaxed">{text}</p></figcaption>
            </figure>)}
          </div>
          <p className="text-sm mt-6 leading-relaxed">{c.schematic}</p>
        </div>
      </section>
      <section className="bg-primary/5 border-y border-border py-12">
        <div className="mx-auto max-w-5xl px-4">
          <p className="text-sm font-bold text-primary mb-3">{c.manufacturer}</p>
          <h2 className="text-2xl md:text-3xl mb-8">{story.platform}</h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <figure className="min-w-0"><img loading="lazy" src={design.url} alt={story.platformCaption} width="414" height="382" className="w-full max-w-md mx-auto" /><figcaption className="mt-4 text-sm leading-relaxed">{story.platformCaption}</figcaption></figure>
            <div className="space-y-5"><p className="text-lg leading-relaxed">{story.platformText}</p><p className="text-sm leading-relaxed border-l-2 border-primary pl-4">{story.orientation}</p></div>
          </div>
        </div>
      </section>
      <section className="pt-12">
        <div className="mx-auto max-w-5xl px-4 pb-8"><h2 className="text-2xl md:text-3xl">{story.title}</h2><p className="mt-4 text-sm leading-relaxed">{c.schematic}</p></div>
        {story.steps.map((step, index) => <article key={step.title} className={index % 2 === 1 ? "bg-primary/5 border-y border-border" : "bg-background"}>
          <div className="mx-auto max-w-5xl px-4 py-10 md:py-14">
            <div className="flex items-start gap-4 mb-8"><span className="text-3xl font-bold text-primary shrink-0" aria-hidden="true">0{index + 1}</span><div><p className="text-sm text-primary font-bold mb-2">{index % 2 === 1 ? c.manufacturer : c.issuesTitle}</p><h3 className="text-2xl md:text-3xl leading-tight">{step.title}</h3></div></div>
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <figure className="min-w-0">
                <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center gap-3">
                  <img loading="lazy" src={stepImages[index]?.url} alt={step.caption} width="414" height="450" className="w-full h-80 md:h-96 object-contain" />
                  <div className="h-80 md:h-96 flex flex-col justify-around text-xs font-bold border-l border-border pl-3"><span>{story.bone}</span><span>{story.gum}</span><span>{story.crown}</span></div>
                </div>
                <figcaption className="mt-4 text-sm leading-relaxed">{step.caption}</figcaption>
              </figure>
              <p className="text-lg leading-relaxed min-w-0">{step.text}</p>
            </div>
          </div>
        </article>)}
        <div className="mx-auto max-w-5xl px-4 py-10"><p className="border-l-4 border-primary bg-primary/5 p-5 text-lg font-semibold leading-relaxed">{story.takeaway}</p><p className="mt-5 text-sm leading-relaxed">{c.limits}</p></div>
      </section>
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl mb-5">{c.evidenceTitle}</h2>
        <p className="leading-relaxed max-w-4xl">{c.evidence}</p>
        <a href="https://pubmed.ncbi.nlm.nih.gov/19722787/" target="_blank" rel="noopener noreferrer" className="block text-primary underline underline-offset-4 mt-5 leading-relaxed text-sm">{c.source}</a>
        <div className="flex flex-wrap gap-4 mt-8">
          <Button asChild><Link to="/research"><BookOpen className="w-4 h-4 mr-2" />{c.research}</Link></Button>
          <Button asChild variant="outline"><Link to="/ti-base-oder-individuelles-abutment">{c.guide}<ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
        </div>
      </section>
      <KnowledgeCheck key={language} copy={c} />
    </main>
    <Footer />
  </div>;
}